import { useEffect, useMemo, useState } from "react";
import {
  Plus, Loader2, ShieldAlert, ShieldCheck, Clock3, Users, FileText,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { AcreedorScreeningDialog } from "@/components/acreedores/AcreedorScreeningDialog";
import { PolizaCard } from "@/components/finanzas/PolizaCard";
import { toast } from "sonner";

const FORMAS = [
  { value: "transferencia", label: "Transferencia (Bancos 1102)" },
  { value: "efectivo", label: "Efectivo (Caja 1101)" },
];

const TIPOS_ACREEDOR = [
  { value: "moral_nacional", label: "Persona moral nacional", id: "RFC (12 caracteres) — obligatorio" },
  { value: "fisica_pfae", label: "Persona física con actividad empresarial", id: "RFC (13 caracteres) — obligatorio" },
  { value: "fisica_nacional", label: "Persona física nacional", id: "RFC o CURP — al menos uno" },
  { value: "extranjera", label: "Persona extranjera", id: "Identificación fiscal del país + país" },
];

const TIPO_ACREEDOR_LABEL = TIPOS_ACREEDOR.reduce((a, o) => ({ ...a, [o.value]: o.label }), {});

// Distintivo del cotejo de listas de la contraparte. Lee `screening_ref` sellado por el backend
// (acreedores_screening.py) — nunca recalcula ni reconsulta.
function BadgeListas({ acreedor, onVer, testid }) {
  const ref = acreedor?.screening_ref;
  if (!acreedor) {
    return <span className="text-xs text-slate-400" data-testid={testid}>—</span>;
  }
  if (!ref) {
    return (
      <Badge variant="outline" className="border-slate-300 text-slate-500" data-testid={testid}>
        Sin cotejar
      </Badge>
    );
  }
  const crit = ref.criticidad || "ninguna";
  const pendiente = !!ref.screening_pendiente;
  const cfg = pendiente
    ? { cls: "border-amber-300 bg-amber-50 text-amber-800", txt: "Pendiente", Icon: Clock3 }
    : crit === "alta"
      ? { cls: "border-rose-300 bg-rose-50 text-rose-800", txt: "Coincidencia", Icon: ShieldAlert }
      : crit === "media"
        ? { cls: "border-amber-300 bg-amber-50 text-amber-800", txt: "Parcial", Icon: ShieldAlert }
        : { cls: "border-emerald-300 bg-emerald-50 text-emerald-800", txt: "Sin coincidencia", Icon: ShieldCheck };
  const { Icon } = cfg;
  return (
    <button type="button" onClick={() => onVer(acreedor.id)}
            className="transition-opacity hover:opacity-75" data-testid={testid}>
      <Badge variant="outline" className={`gap-1 ${cfg.cls}`}>
        <Icon className="h-3 w-3" /> {cfg.txt}
      </Badge>
    </button>
  );
}

function DialogNuevoAcreedor({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tipo_persona: "moral_nacional",
    nombre_o_razon_social: "",
    rfc: "",
    curp: "",
    identificacion_fiscal: "",
    pais: "MX",
    notas: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const tipo = TIPOS_ACREEDOR.find((t) => t.value === form.tipo_persona);

  const submit = async () => {
    if (!form.nombre_o_razon_social.trim()) {
      toast.error("Captura el nombre o la razón social del acreedor."); return;
    }
    setSaving(true);
    try {
      const r = await api.post("/finanzas/acreedores", form);
      if (r.data?.reutilizado) {
        toast.info("Esta contraparte ya estaba registrada. Se reutiliza su cotejo de listas.");
      } else {
        toast.success("Acreedor registrado y cotejado contra listas.");
      }
      setOpen(false);
      setForm({ ...form, nombre_o_razon_social: "", rfc: "", curp: "", identificacion_fiscal: "", notas: "" });
      onCreated?.(r.data?.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="btn-nuevo-acreedor">
          <Users className="mr-2 h-4 w-4" /> Nuevo acreedor
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Alta de acreedor de fondeo</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
            El acreedor es quien <b>entrega recursos</b> a la entidad. Al guardarlo se coteja
            automáticamente contra listas de sanción y PEP. Una coincidencia <b>no impide</b> el
            registro: queda para revisión del Oficial de Cumplimiento.
          </div>
          <div>
            <Label>Tipo de persona *</Label>
            <Select value={form.tipo_persona} onValueChange={set("tipo_persona")}>
              <SelectTrigger data-testid="acr-tipo-persona"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPOS_ACREEDOR.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[11px] text-slate-500">{tipo?.id}</div>
          </div>
          <div>
            <Label>Nombre o razón social *</Label>
            <Input data-testid="acr-nombre" value={form.nombre_o_razon_social}
                   onChange={set("nombre_o_razon_social")}
                   placeholder="Ej. Banco XYZ, S.A. · Financiera ABC" />
          </div>
          {form.tipo_persona !== "extranjera" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>RFC {form.tipo_persona !== "fisica_nacional" ? "*" : ""}</Label>
                <Input data-testid="acr-rfc" value={form.rfc}
                       onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>CURP</Label>
                <Input data-testid="acr-curp" value={form.curp}
                       onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })}
                       disabled={form.tipo_persona === "moral_nacional"} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Identificación fiscal *</Label>
                <Input data-testid="acr-id-fiscal" value={form.identificacion_fiscal}
                       onChange={(e) => setForm({ ...form, identificacion_fiscal: e.target.value.toUpperCase() })}
                       placeholder="Tax ID / VAT" />
              </div>
              <div>
                <Label>País *</Label>
                <Input data-testid="acr-pais" value={form.pais}
                       onChange={(e) => setForm({ ...form, pais: e.target.value.toUpperCase() })}
                       placeholder="US, ES, CA…" />
              </div>
            </div>
          )}
          <div>
            <Label>Notas</Label>
            <Textarea data-testid="acr-notas" value={form.notas} onChange={set("notas")} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="acr-guardar-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar acreedor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogNuevoPrestamo({ onCreated, acreedores }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    acreedor_id: "",
    monto_original: 0,
    tasa_anual: 0,
    plazo_meses: 12,
    fecha_recepcion: new Date().toISOString().slice(0, 10),
    forma: "transferencia",
    banco_receptor: "",
    notas: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.acreedor_id) {
      toast.error("Selecciona al acreedor en el catálogo de contrapartes."); return;
    }
    if (!(form.monto_original > 0)) { toast.error("El monto debe ser mayor a cero."); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        monto_original: Number(form.monto_original),
        tasa_anual: Number(form.tasa_anual),
        plazo_meses: Number(form.plazo_meses) || 0,
      };
      const r = await api.post("/finanzas/prestamos-recibidos", body);
      toast.success(`Préstamo registrado. Póliza ${r.data.poliza?.referencia || ""}`);
      setOpen(false);
      setForm({ ...form, acreedor_id: "", monto_original: 0, tasa_anual: 0, plazo_meses: 12, notas: "" });
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const cortoPlazo = (Number(form.plazo_meses) || 0) <= 12;
  const sel = acreedores?.find((a) => a.id === form.acreedor_id);
  const critSel = sel?.screening_ref?.criticidad;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="btn-nuevo-prestamo" disabled={!acreedores?.length}>
          <Plus className="mr-2 h-4 w-4" /> Registrar préstamo
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Alta de préstamo recibido / Fondeo</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Acreedor *</Label>
            <Select value={form.acreedor_id} onValueChange={set("acreedor_id")}>
              <SelectTrigger data-testid="pr-acreedor">
                <SelectValue placeholder="Selecciona la contraparte…" />
              </SelectTrigger>
              <SelectContent>
                {(acreedores || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.nombre_o_razon_social}{a.rfc ? ` · ${a.rfc}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[11px] text-slate-500">
              El fondeo se amarra a una contraparte del catálogo, ya cotejada contra listas.
            </div>
            {(critSel === "alta" || critSel === "media") && (
              <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2 text-[12px] text-rose-900"
                   data-testid="pr-acreedor-aviso-listas">
                Esta contraparte tiene una coincidencia {critSel === "alta" ? "crítica" : "parcial"} en
                listas. Puedes registrar el préstamo, pero el Oficial de Cumplimiento debe resolverla.
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Monto (MXN) *</Label>
              <MoneyInput
                data-testid="pr-monto"
                value={form.monto_original} onChange={set("monto_original")}
              />
            </div>
            <div>
              <Label>Fecha de recepción</Label>
              <Input
                data-testid="pr-fecha"
                type="date"
                value={form.fecha_recepcion} onChange={set("fecha_recepcion")}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tasa anual (%)</Label>
              <Input
                data-testid="pr-tasa"
                type="number" min="0" step="0.01"
                value={form.tasa_anual} onChange={set("tasa_anual")}
              />
            </div>
            <div>
              <Label>Plazo (meses)</Label>
              <Input
                data-testid="pr-plazo"
                type="number" min="0"
                value={form.plazo_meses} onChange={set("plazo_meses")}
              />
              <div className="mt-1 text-[11px] text-slate-500">
                {cortoPlazo ? "≤ 12 meses → Cuenta 2201 (Corto plazo)" : "> 12 meses → Cuenta 2202 (Largo plazo)"}
              </div>
            </div>
          </div>
          <div>
            <Label>Forma</Label>
            <Select value={form.forma} onValueChange={set("forma")}>
              <SelectTrigger data-testid="pr-forma"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMAS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.forma === "transferencia" && (
            <div>
              <Label>Banco receptor</Label>
              <Input data-testid="pr-banco" value={form.banco_receptor} onChange={set("banco_receptor")} />
            </div>
          )}
          <div>
            <Label>Notas</Label>
            <Textarea data-testid="pr-notas" value={form.notas} onChange={set("notas")} rows={2} />
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-[12px] text-sky-900">
            <div className="font-medium">Póliza automática que se generará:</div>
            <div className="mt-1 font-mono">
              Dr <b>{form.forma === "efectivo" ? "1101 Caja" : "1102 Bancos"}</b> · Cr <b>{cortoPlazo ? "2201" : "2202"}</b>{" — "}
              ${Number(form.monto_original || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="pr-guardar-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar préstamo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogPagoFondeo({ prestamo, open, setOpen, onDone }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    monto_capital: 0,
    monto_interes: 0,
    forma: "transferencia",
    banco_origen: "",
    notas: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const total = (Number(form.monto_capital) || 0) + (Number(form.monto_interes) || 0);

  const submit = async () => {
    if (!(total > 0)) { toast.error("Debes capturar capital y/o interés > 0."); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        monto_capital: Number(form.monto_capital),
        monto_interes: Number(form.monto_interes),
      };
      const r = await api.post(`/finanzas/prestamos-recibidos/${prestamo.id}/pago`, body);
      toast.success(`Pago aplicado. Saldo restante: ${mxn(r.data.saldo_restante)}`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!prestamo) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Pago de fondeo — {prestamo.acreedor}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700">
            Saldo actual: <b>{mxn(prestamo.saldo || 0)}</b> · Cuenta pasivo: <b>{prestamo.cuenta_pasivo}</b>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Capital a pagar</Label>
              <MoneyInput
                data-testid="pgf-capital"
                value={form.monto_capital} onChange={set("monto_capital")}
              />
            </div>
            <div>
              <Label>Interés a pagar</Label>
              <MoneyInput
                data-testid="pgf-interes"
                value={form.monto_interes} onChange={set("monto_interes")}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha</Label>
              <Input data-testid="pgf-fecha" type="date" value={form.fecha} onChange={set("fecha")} />
            </div>
            <div>
              <Label>Forma</Label>
              <Select value={form.forma} onValueChange={set("forma")}>
                <SelectTrigger data-testid="pgf-forma"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMAS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea data-testid="pgf-notas" value={form.notas} onChange={set("notas")} rows={2} />
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-[12px] text-sky-900">
            <div className="font-medium">Póliza automática:</div>
            <div className="mt-1 font-mono">
              {form.monto_capital > 0 && <>Dr <b>{prestamo.cuenta_pasivo}</b> ${Number(form.monto_capital || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}<br /></>}
              {form.monto_interes > 0 && <>Dr <b>5501 Intereses sobre fondeo</b> ${Number(form.monto_interes || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}<br /></>}
              Cr <b>{form.forma === "efectivo" ? "1101 Caja" : "1102 Bancos"}</b> ${total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="pgf-guardar-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogPolizaPrestamo({ prestamo, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const abierto = !!prestamo;

  useEffect(() => {
    if (!prestamo) { setData(null); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/finanzas/prestamos-recibidos/${prestamo.id}/detalle`);
        setData(r.data);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [prestamo]);

  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="dialog-poliza-prestamo">
        <DialogHeader>
          <DialogTitle>Expediente contable del fondeo</DialogTitle>
        </DialogHeader>
        {loading && <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>}
        {!loading && data && (
          <div className="space-y-5">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="font-medium text-slate-900">{data.prestamo?.acreedor}</div>
              <div className="mt-1 grid gap-x-6 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
                <span>Monto original: <b>{mxn(data.prestamo?.monto_original)}</b></span>
                <span>Saldo insoluto: <b>{mxn(data.prestamo?.saldo || 0)}</b></span>
                <span>Capital pagado: <b data-testid="detalle-capital-pagado">{mxn(data.total_capital_pagado)}</b></span>
                <span>Interés pagado: <b data-testid="detalle-interes-pagado">{mxn(data.total_interes_pagado)}</b></span>
                <span>Cuenta de pasivo: <b className="font-mono">{data.prestamo?.cuenta_pasivo}</b></span>
                <span>Plazo: <b>{data.prestamo?.plazo_meses} meses</b></span>
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Póliza de origen</h3>
              {data.poliza_origen
                ? <PolizaCard pz={{ ...data.poliza_origen, origen: "finanzas" }} compacta />
                : <p data-testid="sin-poliza-origen" className="text-sm text-slate-500">
                    Este préstamo no tiene póliza asociada (registro anterior a la integración contable).
                  </p>}
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">
                Historial de pagos ({(data.pagos || []).length})
              </h3>
              {(data.pagos || []).length === 0 && (
                <p data-testid="sin-pagos-fondeo" className="text-sm text-slate-500">
                  Aún no se registran pagos de este préstamo.
                </p>
              )}
              <div className="space-y-4">
                {(data.pagos || []).map((pg) => (
                  <div key={pg.id} data-testid={`pago-fondeo-${pg.id.slice(0, 6)}`}>
                    <div className="mb-1 text-xs text-slate-600">
                      {pg.fecha} · capital <b>{mxn(pg.monto_capital)}</b> · interés{" "}
                      <b>{mxn(pg.monto_interes)}</b>
                    </div>
                    {pg.poliza
                      ? <PolizaCard pz={{ ...pg.poliza, origen: "finanzas" }} compacta />
                      : <p className="text-xs text-slate-500">Sin póliza asociada.</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FondeoRecibido() {
  const [prestamos, setPrestamos] = useState([]);
  const [acreedores, setAcreedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagoSel, setPagoSel] = useState(null);
  const [polizaSel, setPolizaSel] = useState(null);
  const [screeningAcreedorId, setScreeningAcreedorId] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [rp, ra] = await Promise.all([
        api.get("/finanzas/prestamos-recibidos"),
        api.get("/finanzas/acreedores"),
      ]);
      setPrestamos(rp.data.prestamos || []);
      setAcreedores(ra.data.acreedores || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const porId = useMemo(
    () => acreedores.reduce((a, x) => ({ ...a, [x.id]: x }), {}),
    [acreedores]);

  const tot = useMemo(() => {
    const vigentes = prestamos.filter((p) => p.estatus !== "pagado");
    return {
      cantidad: vigentes.length,
      saldo: vigentes.reduce((s, p) => s + (p.saldo || 0), 0),
      original: prestamos.reduce((s, p) => s + (p.monto_original || 0), 0),
      conCoincidencia: acreedores.filter(
        (a) => ["alta", "media"].includes(a.screening_ref?.criticidad)).length,
    };
  }, [prestamos, acreedores]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        title="Préstamos recibidos / Fondeo"
        subtitle="Fondeo bancario y comercial. Cada préstamo genera su póliza (Dr Bancos · Cr Pasivo 2201 a corto plazo o 2202 a largo plazo) y los pagos separan capital + interés (5501). Consulta el asiento y el historial con el botón de póliza. Toda contraparte se coteja contra listas al registrarse."
      />

      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <div className="mb-6 grid gap-4 sm:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Préstamos vigentes</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="stat-pr-vigentes">
              {tot.cantidad}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Saldo insoluto total</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="stat-pr-saldo">
              {mxn(tot.saldo)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Monto original acumulado</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="stat-pr-original">
              {mxn(tot.original)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Contrapartes con coincidencia</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="stat-acr-coincidencias">
              {tot.conCoincidencia}
            </div>
          </div>
        </div>

        <Tabs defaultValue="prestamos" className="w-full">
          <TabsList>
            <TabsTrigger value="prestamos" data-testid="tab-prestamos">Préstamos</TabsTrigger>
            <TabsTrigger value="acreedores" data-testid="tab-acreedores">
              Acreedores ({acreedores.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="prestamos" className="mt-4">
            <div className="mb-3 flex justify-end gap-2">
              <DialogNuevoPrestamo onCreated={cargar} acreedores={acreedores} />
            </div>
            {!loading && acreedores.length === 0 && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                   data-testid="pr-sin-acreedores">
                Registra primero al acreedor en la pestaña <b>Acreedores</b>. El fondeo debe quedar
                amarrado a una contraparte identificada y cotejada contra listas.
              </div>
            )}

            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Acreedor</TableHead>
                    <TableHead>Listas</TableHead>
                    <TableHead className="text-right">Monto original</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Tasa</TableHead>
                    <TableHead>Plazo</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead>Estatus</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell></TableRow>
                  )}
                  {!loading && prestamos.length === 0 && (
                    <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-8">
                      Sin préstamos registrados. Registra el primer fondeo para que se refleje en tesorería.
                    </TableCell></TableRow>
                  )}
                  {prestamos.map((p) => (
                    <TableRow key={p.id} data-testid={`row-pr-${p.id.slice(0,6)}`}>
                      <TableCell>{p.fecha_recepcion}</TableCell>
                      <TableCell className="font-medium">{p.acreedor}</TableCell>
                      <TableCell>
                        <BadgeListas acreedor={porId[p.acreedor_id]} onVer={setScreeningAcreedorId}
                                     testid={`pr-listas-badge-${p.id.slice(0,6)}`} />
                      </TableCell>
                      <TableCell className="text-right">{mxn(p.monto_original)}</TableCell>
                      <TableCell className="text-right font-semibold">{mxn(p.saldo || 0)}</TableCell>
                      <TableCell>{(p.tasa_anual || 0).toFixed(2)}%</TableCell>
                      <TableCell>{p.plazo_meses || "—"} meses</TableCell>
                      <TableCell className="font-mono text-xs">{p.cuenta_pasivo}</TableCell>
                      <TableCell>
                        <Badge className={p.estatus === "pagado" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                          {p.estatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setPolizaSel(p)}
                            data-testid={`btn-poliza-fondeo-${p.id.slice(0,6)}`}
                            title="Ver la póliza de origen y el historial de pagos con su PDF"
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            disabled={p.estatus === "pagado"}
                            onClick={() => { setPagoSel(p); setPagoOpen(true); }}
                            data-testid={`btn-pago-fondeo-${p.id.slice(0,6)}`}
                          >
                            Pago
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="acreedores" className="mt-4">
            <div className="mb-3 flex justify-end">
              <DialogNuevoAcreedor onCreated={(id) => { cargar(); if (id) setScreeningAcreedorId(id); }} />
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre / Razón social</TableHead>
                    <TableHead>Identificador</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Listas</TableHead>
                    <TableHead className="text-right">Préstamos</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell></TableRow>
                  )}
                  {!loading && acreedores.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">
                      Sin acreedores registrados. Da de alta la primera contraparte de fondeo.
                    </TableCell></TableRow>
                  )}
                  {acreedores.map((a) => {
                    const suyos = prestamos.filter((p) => p.acreedor_id === a.id);
                    return (
                      <TableRow key={a.id} data-testid={`row-acr-${a.id.slice(0,6)}`}>
                        <TableCell className="font-medium">{a.nombre_o_razon_social}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {a.rfc || a.curp || a.identificacion_fiscal || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {TIPO_ACREEDOR_LABEL[a.tipo_persona] || a.tipo_persona}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <BadgeListas acreedor={a} onVer={setScreeningAcreedorId}
                                       testid={`acr-listas-badge-${a.id.slice(0,6)}`} />
                        </TableCell>
                        <TableCell className="text-right">{suyos.length}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {mxn(suyos.reduce((s, p) => s + (p.saldo || 0), 0))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        <DialogPagoFondeo
          prestamo={pagoSel}
          open={pagoOpen}
          setOpen={setPagoOpen}
          onDone={cargar}
        />

        <DialogPolizaPrestamo prestamo={polizaSel} onClose={() => setPolizaSel(null)} />

        <AcreedorScreeningDialog
          acreedorId={screeningAcreedorId}
          open={!!screeningAcreedorId}
          onOpenChange={(v) => { if (!v) setScreeningAcreedorId(null); }}
          onRecotejado={cargar}
        />
      </div>
    </div>
  );
}
